import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Chip, Divider,
  Button, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReactECharts from './Chart';
import { useNavigate } from 'react-router-dom';
import PageGuideTooltip from './PageGuideTooltip';
import WinRateLiftCard from './WinRateLiftCard';
import AttachmentRateCard from './AttachmentRateCard';
import { buildQuarterOptions, defaultPeriodValue } from '../utils/quarterOptions';
import DrillPanel from './exec/DrillPanel';
import WinRateDrillPanel from './exec/WinRateDrillPanel';
import { makeOverviewOppConfig, makeOverviewWorkshopOppConfig, makeInfluenceDrillConfig } from './exec/drillConfigs';
import useTags from '../hooks/useTags';
import { formatCurrency } from '../utils/formatCurrency';
import WorkshopLink from './WorkshopLink';
import ExportMenu from './ExportMenu';
import useUrlState from '../hooks/useUrlState';
import { fmtWinRate, winRateInline, fmtCompactMoney, matureWinRates, YOUNG_QUARTERS_NOTE } from '../utils/winRate';
import WinRateWithOpen from './WinRateWithOpen';
import InfluenceSummaryStrip from './exec/InfluenceSummaryStrip';
import InfluenceKpiCards from './exec/InfluenceKpiCards';
import useInfluenceKpis from '../hooks/useInfluenceKpis';
import useInfluenceBlock from '../hooks/useInfluenceBlock';
import { adaptTrend, PREVIOUS_BASIS, blockReasonText, isReloading, reloadingText, drillOn } from '../utils/influenceBlocks';
import { kpiCardsActive } from '../utils/influenceKpis';
import ExecPageHeader from './exec/ExecPageHeader';
import ExecFilterBar from './exec/ExecFilterBar';
import { ExecKpiRow, ExecKpiSlot, KpiTile as KPITile, kpiValueSize } from './exec/ExecKpiRow';
import {
  kpiBasisActive, basisName, BasisLine, BasisSubLines, basisWinRateText, basisTooltip, basisFallbackText,
} from './exec/KpiBasis';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const fmtCurrency = (v) => formatCurrency(v, { emptyDisplay: '—' });
const fmtMultiplier = (v) => v == null ? '—' : `${v.toFixed(1)}x`;
const fmtPct = fmtWinRate;
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const COMPARISON_EXPORT_COLUMNS = [
  { header: 'Group', key: 'group' },
  { header: 'Win Rate', key: 'winRate' },
  { header: 'Closed n', key: 'n' },
  { header: 'Still open', key: 'openShare' },
  { header: 'Avg Deal Size', key: 'avgDealSize' },
  { header: 'Velocity Value', key: 'velocityValue' },
];

const BOT_EXPORT_COLUMNS = [
  { header: 'Domain', key: 'domain' },
  { header: 'Status', key: 'status' },
  { header: 'Participants', key: 'participant_count' },
  { header: 'Bot Score', key: 'bot_score' },
  { header: 'Reason', key: 'reason' },
];

const WORKSHOP_EXPORT_COLUMNS = [
  { header: '#', key: 'rank' },
  { header: 'Workshop', key: 'name' },
  { header: 'Attendees', key: 'attendees' },
  { header: 'Total opportunity value', key: 'pipeline' },
  { header: 'Closed n', key: 'win_rate_n' },
  { header: 'Still open', key: 'open_share_text' },
  { header: 'Won', key: 'won' },
  { header: 'Win Rate', key: 'win_rate' },
  { header: '$/Attendee', key: 'pipeline_per_attendee' },
];

function ComparisonDrillPanel({ title, rows, onClose }) {
  return (
    <Paper variant="outlined" sx={{ mt: 1, p: 2, borderLeft: '4px solid #1976d2' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
        <ExportMenu title={title} data={rows} columns={COMPARISON_EXPORT_COLUMNS} />
        <Button size="small" onClick={onClose} sx={{ minWidth: 0, p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </Button>
      </Box>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Group</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Win Rate</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Avg Deal Size</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Velocity Value</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Closed n</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Still open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.group} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{row.group}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: row.winRateColor || 'inherit' }}>{row.winRate}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>{row.avgDealSize}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', fontWeight: 600, color: '#1976d2' }}>{row.velocityValue}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: 'text.secondary' }}>{row.n}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: 'text.secondary' }}>{row.openShare}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Baseline = closed deals with no linked TEC check-in. An audit found some of them had an attending SE on the deal team (see Why trust this number? on the win rate lift tile), so the gap is understated until the corrected baseline replaces it.
      </Typography>
    </Paper>
  );
}

function PipelineInfluencedCard({ overview, expandedCard, onToggle }) {
  const basisOn = kpiBasisActive(overview);
  const basis = overview?.kpi_basis;
  const total = overview?.total_pipeline_influenced;
  const seAmt = overview?.se_pipeline;
  const extAmt = overview?.external_pipeline;
  const partnerAmt = overview?.partner_channel_pipeline;
  const segments = [
    { key: 'pipeline_se', label: 'SE (Fortinet)', value: seAmt },
    { key: 'pipeline_external', label: 'External', value: extAmt },
    {
      key: 'pipeline_partner',
      label: 'Channel Partner Sourced',
      value: partnerAmt,
      tooltip: 'Total opportunity value from deals sourced by channel partners linked to TEC workshops. Tracked via partner_opportunity table — separate from the headline figure, which counts opps where a partner contact directly attended a workshop.',
    },
  ];
  return (
    <Paper
      elevation={2}
      aria-label="Pipeline Influenced card"
      sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Total opportunity value{basisOn ? ` · ${basisName(basis)}` : ''}
        </Typography>
        <PageGuideTooltip title="Total opportunity value" description={basisOn ? basisTooltip(basis, 'Whole deal amount of deals where an SE or technical team member (or contact) attended a TEC workshop or event before the deal closed. Reach, not revenue: includes open, won and lost. The definition is much broader than the older attendee-linked population, so values rose because the definition widened, not because outcomes improved.') : "Total opportunities linked to any TEC attendee. Each opportunity is attributed to the highest-priority attendee type present: SE (Fortinet employee attended) > External (no SE present). Channel Partner Sourced below is a separate, program-level figure from partner_opportunity — not a per-opportunity attendee-type tier, since attributing a partner account's full book of business to individual opportunities overstates 9-13x at this data volume. Total opportunity value includes open, won, and closed-lost — program reach metric, not a revenue metric (open-only deals are called Open pipeline elsewhere). Click a segment row to see linked opportunities. Segments reflect independent cohort cuts of the program — not mutually exclusive additive sub-totals. An opportunity may appear in multiple segment views."} metricKey="pipeline_influenced" />
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', ...kpiValueSize(fmtCurrency(total)) }}>
        {fmtCurrency(total)}
      </Typography>
      {basisOn ? (
        <>
          <Typography variant="caption" color="text.secondary">
            {basis.n_deals != null ? `${Number(basis.n_deals).toLocaleString()} deals · ` : ''}all stages
          </Typography>
          <BasisLine basis={basis} />
          <Divider sx={{ my: 0.5 }} />
          <BasisSubLines basis={basis} field="value" />
          {overview?.legacy_kpis?.total_pipeline_influenced != null && (
            <Typography variant="caption" color="text.secondary" aria-label="Previous definition">
              Previous attendee-linked definition: {fmtCurrency(overview.legacy_kpis.total_pipeline_influenced)}
            </Typography>
          )}
        </>
      ) : (<>
      <Typography variant="caption" color="text.secondary">
        {overview?.total_attendees?.toLocaleString()} attendees · {overview?.total_workshops} workshops · all stages
      </Typography>
      {basisFallbackText(overview) && (
        <Typography variant="caption" color="text.secondary" aria-label="KPI basis">{basisFallbackText(overview)}</Typography>
      )}
      <Divider sx={{ my: 0.5 }} />
      {segments.map(({ key, label, value, tooltip }) => (
        <Box
          key={key}
          onClick={() => onToggle(key)}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 0.75, py: 0.375, borderRadius: 0.75, cursor: 'pointer',
            backgroundColor: expandedCard === key ? '#e3f2fd' : 'transparent',
            '&:hover': { backgroundColor: expandedCard === key ? '#e3f2fd' : '#f5f5f5' },
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {tooltip ? (
              <Tooltip title={tooltip} placement="top" arrow>
                <span style={{ borderBottom: '1px dotted #999', cursor: 'help' }}>{label}</span>
              </Tooltip>
            ) : label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2' }}>
            {fmtCurrency(value)}
          </Typography>
        </Box>
      ))}
      </>)}
    </Paper>
  );
}

export default function ExecSummary() {
  const navigate = useNavigate();
  const quarterOptions = useMemo(buildQuarterOptions, []);
  const defaultQuarter = defaultPeriodValue(quarterOptions) || '';
  const [selectedQuarter, setSelectedQuarter] = useUrlState('q', defaultQuarter, { allowed: quarterOptions.map(o => o.value) });
  // null | 'velocity' | 'winrate' | 'pipeline_se' | 'pipeline_external' | 'pipeline_partner' | 'top_workshop' | 'attachment'
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedWorkshop, setExpandedWorkshop] = useState(null);

  const [customStart, setCustomStart] = useUrlState('start', '', { replace: true });
  const [customEnd, setCustomEnd] = useUrlState('end', '', { replace: true });

  const selectedOption = quarterOptions.find(o => o.value === selectedQuarter);
  const startDate = selectedQuarter === 'custom' ? customStart : (selectedOption?.startDate || '');
  const endDate = selectedQuarter === 'custom' ? customEnd : (selectedOption?.endDate || '');

  const handleQuarterChange = (e) => {
    const val = e.target.value;
    setSelectedQuarter(val);
    if (val !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
  };

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const [workshops, setWorkshops] = useState(null);
  const [botSummary, setBotSummary] = useState(null);
  const [eventFilter, setEventFilter] = useUrlState('event', '', { replace: true });
  const [eventOptions, setEventOptions] = useState([]);
  const [tecProgramFilter, setTecProgramFilter] = useUrlState('program', '', { replace: true });
  const { tagsByCategory } = useTags();
  const tecProgramOptions = tagsByCategory?.['TEC Program']?.tags || [];
  const kpis = useInfluenceKpis({ startDate, endDate, tecProgramTagId: tecProgramFilter, tier: 'program', eventId: eventFilter }, !!(startDate && endDate));
  const kpiCardsOn = kpiCardsActive(overview?.influence_cutover, kpis.data);
  const legacyMoneyHidden = kpiCardsOn;
  // Plans 2194/2200: with the cutover ON the quarterly trend reads the influenced rows; a missing row falls back to the previous
  // definition with a visible reason. The workshop table, Top Workshop tile and Bot Participants leave the page (Workshop Index
  // and the participants page carry them).
  const trendBlock = useInfluenceBlock('/exec/influence/trend', { tecProgramTagId: tecProgramFilter }, !!overview?.influence_cutover);
  const trendInfluenced = useMemo(() => adaptTrend(trendBlock.data), [trendBlock.data]);
  // Plan 2213 phase 4: with the deal lists on, a trend bar and the attachment card open the influenced deals behind them.
  const infStatus = useInfluenceBlock('/exec/influence/status', {}, !!overview?.influence_cutover);
  const summaryDrillOn = !!overview?.influence_cutover && drillOn(infStatus.data, 'summary');
  const [trendDrill, setTrendDrill] = useState(null); // { of, state, title }
  const [attachmentThreshold, setAttachmentThreshold] = useState(50000);
  const trendOn = !!trendInfluenced;
  const trendReloading = isReloading(trendBlock.data); // plan 2213: reloading never falls back to the previous definition
  const blockFallbackReason = (blk) => (overview?.influence_cutover && blk.data && blk.data.available === false
    ? blockReasonText(blk.data.reason) : null);
  const [digestSubscribed, setDigestSubscribed] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [botPanelOpen, setBotPanelOpen] = useState(false);
  const botFlaggedCount = (botSummary?.bots ?? 0) + (botSummary?.suspicious ?? 0);

  // Bot summary + top-workshops both key off the period alone (independent of
  // eventFilter and of each other) — fire concurrently instead of as two
  // separately-scheduled effects.
  useEffect(() => {
    if (!startDate || !endDate) return;
    const periodParams = new URLSearchParams({ startDate, endDate });
    fetch(`${API_BASE_URL}/exec/filtered/bot-participants?${periodParams}`, { credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(d => setBotSummary(d))
      .catch(() => setBotSummary(null));
  }, [startDate, endDate]);

  // Plan 2213 group 1: the old top-workshops table only renders with the cutover OFF, so with it on the old-pool query never runs
  // (the Workshop Index on Deep Dive carries the influenced view). Waits for the overview, which says which basis the page is on.
  const overviewSettled = overview != null || overviewError != null;
  const cutoverOn = !!overview?.influence_cutover;
  useEffect(() => {
    if (!startDate || !endDate || !overviewSettled || cutoverOn) return;
    const workshopParams = new URLSearchParams({ startDate, endDate, limit: '10' });
    fetch(`${API_BASE_URL}/exec/overview/workshops?${workshopParams}`, { credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(d => setWorkshops(d.rows || []))
      .catch(() => setWorkshops([]));
  }, [startDate, endDate, overviewSettled, cutoverOn]);

  // Mount-only fetches with no dependency on period/filter state or on each
  // other — fire concurrently on first render.
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/events`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          const list = d?.events || [];
          const now = new Date().toISOString();
          const past = list.filter(e => e.start_datetime < now && (e.registration_count > 0 || e.status === 'ended'));
          setEventOptions(past);
        })
        .catch(() => setEventOptions([])),
      fetch(`${API_BASE_URL}/exec-digest/subscription`, { credentials: 'include' })
        .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
        .then(d => setDigestSubscribed(!!d.subscribed))
        .catch(() => setDigestSubscribed(null)),
    ]);
  }, []);

  // Plan 2213 group 1: the previous-definition trend loads only when the page is not on the influenced basis (cutover off) or the
  // influenced series has neither arrived nor said "reloading". With the service on, basis=influenced makes the API answer from it and an
  // `available:false` answer leaves the chart empty; with the service off (nightly rows) this is the labelled previous definition.
  useEffect(() => {
    const blockAnswered = trendBlock.data != null || trendBlock.error != null; // `loading` is still false in the render that enables the block
    if (!overviewSettled || (cutoverOn && (!blockAnswered || trendInfluenced || trendReloading))) { setTrendLoading(false); return undefined; } // a superseded fetch's finally is skipped
    let live = true;
    setTrendLoading(true);
    const params = new URLSearchParams({ windowMonths: '24', postWorkshopOnly: 'false' });
    if (cutoverOn) params.set('basis', 'influenced');
    fetch(`${API_BASE_URL}/exec/quarterly-trend?${params}`, { credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(d => { if (live) setTrendData(d?.available === false ? null : d); })
      .catch(() => { if (live) setTrendData(null); })
      .finally(() => { if (live) setTrendLoading(false); });
    return () => { live = false; };
  }, [overviewSettled, cutoverOn, trendBlock.data, trendBlock.error, trendInfluenced, trendReloading]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    setOverviewLoading(true);
    setOverviewError(null);
    const params = new URLSearchParams({ startDate, endDate });
    if (eventFilter) params.set('event_id', eventFilter);
    if (tecProgramFilter) params.set('tecProgramTagId', tecProgramFilter);
    fetch(`${API_BASE_URL}/exec/overview?${params}`, { credentials: 'include' })
      .then(res => {
        if (res.status === 403) throw Object.assign(new Error('exec:read permission required'), { status: 403 });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setOverview)
      .catch(err => setOverviewError(err))
      .finally(() => setOverviewLoading(false));
  }, [startDate, endDate, eventFilter, tecProgramFilter]);

  const toggleDigestSubscription = async () => {
    if (digestSubscribed === null || digestBusy) return;
    setDigestBusy(true);
    try {
      const action = digestSubscribed ? 'unsubscribe' : 'subscribe';
      const r = await fetch(`${API_BASE_URL}/exec-digest/${action}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDigestSubscribed(!!d.subscribed);
    } catch {
      // leave state unchanged on failure
    } finally {
      setDigestBusy(false);
    }
  };

  const trendShown = trendOn ? trendInfluenced : trendReloading ? null : trendData;
  const onTrendBarClick = (ev) => {
    const q = trendShown?.quarters?.[ev.dataIndex];
    if (!q || (ev.seriesName !== 'Total opportunity value' && ev.seriesName !== 'Won')) return;
    const state = ev.seriesName === 'Won' ? 'won' : 'all';
    const of = `quarter:${q.replace('-Q', 'Q')}`;
    setTrendDrill((prev) => (prev && prev.of === of && prev.state === state ? null : { of, state, title: `${q} — ${state === 'won' ? 'won ' : ''}influenced deals` }));
  };
  const trendMature = useMemo(
    () => (trendShown?.quarters?.length ? matureWinRates(trendShown.quarters, trendShown.win_rate) : null),
    [trendShown],
  );
  const trendOption = useMemo(() => {
    if (!trendShown?.quarters?.length) return null;
    return {
      grid: { containLabel: true, top: 50, bottom: 20, left: 20, right: 20 },
      legend: { top: 5, data: ['Total opportunity value', 'Won', 'Win Rate'] },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params];
          const i = arr[0]?.dataIndex;
          const lines = [`<strong>${arr[0]?.axisValueLabel ?? ''}</strong>`];
          arr.forEach((p) => {
            if (p.seriesName === 'Win Rate' && trendMature.young[i]) {
              lines.push(`${p.marker} Win Rate: — (recent quarter, still open)`);
            } else if (p.seriesName === 'Win Rate') {
              lines.push(`${p.marker} Win Rate: ${winRateInline(p.value, { n: trendShown.win_rate_n?.[i], openShare: trendShown.open_share?.[i] })}`);
            } else {
              lines.push(`${p.marker} ${p.seriesName}: ${fmtCurrency(p.value)}`);
            }
          });
          return lines.join('<br/>');
        },
      },
      xAxis: { type: 'category', data: trendShown.quarters, axisLabel: { rotate: 30, fontSize: 11 } },
      yAxis: [
        { type: 'value', name: 'Total opportunity value ($)', axisLabel: { formatter: fmtCompactMoney } },
        { type: 'value', name: 'Win Rate', axisLabel: { formatter: (v) => `${(v * 100).toFixed(0)}%` } },
      ],
      series: [
        {
          name: 'Total opportunity value',
          type: 'bar',
          data: trendShown.pipeline,
          itemStyle: { color: '#1976d2' },
          yAxisIndex: 0,
        },
        {
          name: 'Won',
          type: 'bar',
          data: trendShown.won,
          itemStyle: { color: '#2e7d32' },
          yAxisIndex: 0,
          stack: 'closed',
        },
        {
          name: 'Win Rate',
          type: 'line',
          data: trendMature.rates,
          yAxisIndex: 1,
          itemStyle: { color: '#ed6c02' },
          lineStyle: { width: 2 },
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 6,
          markLine: {
            silent: true,
            symbol: 'none',
            data: [{ type: 'max', name: 'Best Quarter', yAxis: undefined }],
            label: { formatter: (p) => `Best: ${(p.value * 100).toFixed(0)}%`, position: 'end', fontSize: 10 },
            lineStyle: { type: 'dashed', color: '#bbb', width: 1 },
          },
        },
      ],
    };
  }, [trendShown, trendMature]);

  const drillRows = useMemo(() => {
    if (!overview) return null;
    const attWr = overview.win_rate_attendees;
    const blWr = overview.win_rate_baseline;
    const attVel = overview.velocity_attendee_value;
    const blVel = overview.velocity_baseline_value;
    const attN = overview.win_rate_attendee_n_participant_pool;
    const blN = overview.win_rate_baseline_n;
    const attAds = (attWr != null && attWr > 0 && attVel != null) ? attVel / attWr : null;
    const blAds = (blWr != null && blWr > 0 && blVel != null) ? blVel / blWr : null;
    return [
      {
        group: 'Attendees',
        winRate: fmtPct(attWr),
        winRateColor: attWr != null && blWr != null && attWr < blWr ? '#c62828' : '#2e7d32',
        avgDealSize: fmtCurrency(attAds),
        velocityValue: fmtCurrency(attVel),
        n: attN != null ? attN.toLocaleString() : '—',
        openShare: overview.win_rate_attendee_open_share != null ? `${Math.round(overview.win_rate_attendee_open_share * 100)}%` : '—',
      },
      {
        group: 'Non-Attendees',
        winRate: fmtPct(blWr),
        avgDealSize: fmtCurrency(blAds),
        velocityValue: fmtCurrency(blVel),
        n: blN != null ? blN.toLocaleString() : '—',
        openShare: overview.win_rate_baseline_open_share != null ? `${Math.round(overview.win_rate_baseline_open_share * 100)}%` : '—',
      },
    ];
  }, [overview]);

  const toggleCard = (card) => setExpandedCard(prev => prev === card ? null : card);
  const toggleWorkshop = (name) => setExpandedWorkshop(prev => prev === name ? null : name);

  const pipelineDrillConfig = useMemo(() => {
    if (expandedCard === 'pipeline_se') return makeOverviewOppConfig(null, 'internal');
    if (expandedCard === 'pipeline_external') return makeOverviewOppConfig(null, 'external');
    if (expandedCard === 'pipeline_partner') return makeOverviewOppConfig(null, null, 'channel');
    if (expandedCard === 'attachment' && overview?.influence_cutover) {
      // the influenced card lists the influenced attached deals; the previous-definition list would not add up to it
      return summaryDrillOn ? makeInfluenceDrillConfig('attachment:attached', { threshold: attachmentThreshold, surface: 'summary' }) : null;
    }
    if (expandedCard === 'attachment') return makeOverviewOppConfig(null, 'internal');
    if (expandedCard === 'top_workshop' && overview?.top_workshop?.name)
      return makeOverviewWorkshopOppConfig(overview.top_workshop.name);
    return null;
  }, [expandedCard, overview, summaryDrillOn, attachmentThreshold]);

  const pipelineDrillTitle = {
    pipeline_se: 'SE-Influenced Opportunities',
    pipeline_external: 'External-Influenced Opportunities',
    pipeline_partner: 'Channel Partner Sourced Opportunities',
    attachment: overview?.influence_cutover ? 'Deals with a TEC-trained SE on the deal' : 'SE-Attached Opportunities (Internal Participants)',
    top_workshop: overview?.top_workshop ? `Opportunities — ${overview.top_workshop.name}` : 'Top Workshop Opportunities',
  };

  const workshopDrillConfig = useMemo(
    () => expandedWorkshop ? makeOverviewWorkshopOppConfig(expandedWorkshop) : null,
    [expandedWorkshop],
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <ExecPageHeader
        title="Executive Summary"
        subtitle="Total Program Influence"
        guide={{
          title: 'Executive Summary',
          description: 'Top-line TEC program KPIs: pipeline influenced, won revenue, velocity multiplier, win rate lift, and SE attachment rate — all compared to uninfluenced opportunities.',
          howtoHref: '/howto#entry-294',
        }}
        actions={(
          <>
            {digestSubscribed !== null && (
              <Tooltip title={digestSubscribed
                ? 'You receive the weekly exec digest email. Click to unsubscribe.'
                : 'Get these KPIs emailed every Monday morning.'}
              >
                <Button
                  size="small"
                  variant={digestSubscribed ? 'contained' : 'outlined'}
                  color={digestSubscribed ? 'success' : 'primary'}
                  aria-label="weekly digest subscription"
                  startIcon={<MailOutlineIcon sx={{ fontSize: 15 }} />}
                  onClick={toggleDigestSubscription}
                  disabled={digestBusy}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                >
                  {digestSubscribed ? 'Weekly email: on' : 'Email me this weekly'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="View measurement methodology">
              <Button
                size="small"
                startIcon={<MenuBookIcon sx={{ fontSize: 15 }} />}
                onClick={() => navigate('/methodology')}
                sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}
              >
                Methodology
              </Button>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => navigate('/exec/filtered-brief?q=' + selectedQuarter)}
            >
              Deep Dive
            </Button>
          </>
        )}
      />
      <ExecFilterBar
        quarterOptions={quarterOptions}
        selectedQuarter={selectedQuarter}
        onQuarterChange={handleQuarterChange}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStart={setCustomStart}
        onCustomEnd={setCustomEnd}
        eventOptions={eventOptions}
        eventFilter={eventFilter}
        onEventFilter={setEventFilter}
        tecProgramOptions={tecProgramOptions}
        tecProgramFilter={tecProgramFilter}
        onTecProgramFilter={setTecProgramFilter}
      />

      {/* Scope notice — always visible, includes freshness when available */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{ mb: 2, borderLeft: '4px solid #1976d2', '& .MuiAlert-icon': { color: '#1976d2' } }}
      >
        <strong>Total Program Influence</strong> — counts full Salesforce deal value for every opportunity touched by a TEC attendee. All products · all stages (open, won, closed-lost).
        Numbers are larger than <strong>Deep Dive (TEC Product Attribution)</strong>, which counts only the product-tagged line-item subtotal for each deal.
        {overview?.last_sf_sync && (
          <> · Salesforce data as of {fmtDate(overview.last_sf_sync)}</>
        )}
      </Alert>

      {/* Event + TEC Program filter chips */}
      {(eventFilter || tecProgramFilter) && (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {eventFilter && (() => {
            const ev = eventOptions.find(e => String(e.id) === eventFilter);
            return ev ? (
              <Chip
                label={`Filtered: ${ev.title}`}
                onDelete={() => setEventFilter('')}
                color="primary"
                size="small"
                deleteIcon={<CloseIcon />}
              />
            ) : null;
          })()}
          {tecProgramFilter && (() => {
            const tp = tecProgramOptions.find(t => String(t.id) === tecProgramFilter);
            return tp ? (
              <Chip
                label={`TEC Program: ${tp.name}`}
                onDelete={() => setTecProgramFilter('')}
                color="primary"
                size="small"
                deleteIcon={<CloseIcon />}
              />
            ) : null;
          })()}
        </Box>
      )}

      {/* KPI cards */}
      {overviewLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {overviewError && (
        <Alert severity={overviewError.status === 403 ? 'info' : 'error'} sx={{ mb: 3 }}>
          {overviewError.status === 403
            ? 'Executive analytics require exec access. Contact your administrator.'
            : `Unable to load executive overview: ${overviewError.message}`}
        </Alert>
      )}

      {overview && !overviewLoading && (
        <>
          {kpiCardsOn && <InfluenceKpiCards kpis={kpis} tier="program" onCardClick={() => navigate('/exec/influence')}
            drill={{ surface: 'summary', scope: { startDate, endDate, tecProgramTagId: tecProgramFilter, eventId: eventFilter } }} />}
          {!kpiCardsOn && <InfluenceSummaryStrip cutover={!!overview.influence_cutover} />}

          <ExecKpiRow>
            {!overview.influence_cutover && (<>
            <ExecKpiSlot basis={180}>
              <KPITile
                label="Velocity Multiplier"
                value={fmtMultiplier(overview.velocity_multiplier)}
                sub={overview.baseline_available ? 'vs Non-Attendees' : 'Baseline not available'}
                sub2={
                  overview.win_rate_attendee_n_participant_pool != null && overview.win_rate_baseline_n != null
                    ? `n=${overview.win_rate_attendee_n_participant_pool.toLocaleString()} attendees · n=${overview.win_rate_baseline_n.toLocaleString()} baseline (closed)`
                    : undefined
                }
                color={overview.velocity_multiplier != null && overview.velocity_multiplier >= 1.5 ? '#2e7d32' : undefined}
                aria-label="Velocity Multiplier card" metricKey="velocity_multiplier"
                tooltip="Ratio of sales velocity (win rate × avg deal size) for SE-engaged vs. non-engaged opportunities. Baseline = closed deals from accounts with zero TEC workshop attendance — any account where at least one employee attended a TEC workshop is excluded from baseline entirely. Driven primarily by larger deal sizes on SE-engaged opportunities. Click to see the comparison breakdown."
                onClick={() => toggleCard('velocity')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 0.5, fontStyle: 'italic' }}>
                Full program (all products) · See Filtered Brief for product-tagged view
              </Typography>
            </ExecKpiSlot>
            <ExecKpiSlot basis={180}>
              <WinRateLiftCard
                attendeeWinRate={overview.win_rate_attendees}
                attendeeN={overview.win_rate_attendee_n_participant_pool}
                attendeeOpenShare={overview.win_rate_attendee_open_share}
                baselineWinRate={overview.win_rate_baseline}
                baselineN={overview.win_rate_baseline_n}
                baselineOpenShare={overview.win_rate_baseline_open_share}
                regression={overview.win_rate_regression}
                onClick={() => toggleCard('winrate')}
              />
            </ExecKpiSlot>
            </>)}
            {!legacyMoneyHidden && (<>
            <ExecKpiSlot basis={220}>
              <PipelineInfluencedCard
                overview={overview}
                expandedCard={expandedCard}
                onToggle={toggleCard}
              />
            </ExecKpiSlot>
            <ExecKpiSlot basis={180}>
              {kpiBasisActive(overview) ? (
                <KPITile
                  label={`Total Won · ${basisName(overview.kpi_basis)}`}
                  value={fmtCurrency(overview.total_won)}
                  sub={basisWinRateText(overview.kpi_basis)}
                  sub2={(
                    <>
                      <BasisLine basis={overview.kpi_basis} />
                      <BasisSubLines basis={overview.kpi_basis} field="won_value" />
                    </>
                  )}
                  color="#2e7d32"
                  aria-label="Total Won card" metricKey="total_won"
                  tooltip={basisTooltip(overview.kpi_basis, 'Closed-won amount of influenced deals created in the window. The win rate shown is the raw, unadjusted rate over closed deals; the adjusted lift is in the influence scorecard.')}
                />
              ) : (
              <KPITile
                label="Total Won"
                value={fmtCurrency(overview.total_won)}
                sub={'Win rate ' + winRateInline(overview.win_rate_attendees, { n: overview.win_rate_attendee_n_participant_pool, openShare: overview.win_rate_attendee_open_share })}
                sub2={basisFallbackText(overview) || 'All products · click for full pipeline detail'}
                color="#2e7d32"
                aria-label="Total Won card" metricKey="total_won"
                tooltip="Closed-Won revenue from Salesforce opportunities linked to TEC workshop attendees in the selected period. Not filtered to tagged products. Click to see the Deep Dive."
                onClick={() => navigate('/exec/filtered-brief?q=' + selectedQuarter)}
              />
              )}
            </ExecKpiSlot>
            </>)}
            {startDate && endDate && (
              <ExecKpiSlot basis={180}>
                <AttachmentRateCard
                  startDate={startDate}
                  endDate={endDate}
                  onClick={(threshold) => { if (threshold) setAttachmentThreshold(threshold); toggleCard('attachment'); }}
                  influenceCutover={!!overview.influence_cutover}
                  canOpen={summaryDrillOn}
                  onThresholdChange={setAttachmentThreshold}
                />
              </ExecKpiSlot>
            )}
            {overview.top_workshop && !overview.influence_cutover && (
              <ExecKpiSlot basis={180}>
                <KPITile
                  label="Top Workshop"
                  value={overview.top_workshop.name}
                  sub={`${overview.top_workshop.attendees.toLocaleString()} attendees`}
                  sub2={fmtCurrency(overview.top_workshop.pipeline_per_attendee) + ' / attendee'}
                  color="#7b1fa2"
                  aria-label="Top Workshop card" metricKey="workshop_ranking"
                  tooltip="Highest-value workshop in the selected period, ranked by total closed-won revenue. The per-attendee figure is the workshop's total opportunity value (all stages) ÷ unique attendees. Click to see linked opportunities."
                  valueStyle={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.2 }}
                  onClick={() => toggleCard('top_workshop')}
                />
              </ExecKpiSlot>
            )}
            {botSummary !== null && !overview.influence_cutover && (
              <ExecKpiSlot basis={180}>
                <KPITile
                  label="Bot Participants"
                  value={botFlaggedCount.toLocaleString()}
                  sub={botFlaggedCount > 0 ? `${botSummary.bots ?? 0} bots · ${botSummary.suspicious ?? 0} suspicious` : 'No flagged domains'}
                  color={botFlaggedCount > 0 ? '#d32f2f' : '#757575'}
                  aria-label="Bot Participants card"
                  tooltip="Participants flagged as bots or suspicious based on domain reputation. Click to see flagged domains."
                  onClick={() => setBotPanelOpen(p => !p)}
                />
              </ExecKpiSlot>
            )}
          </ExecKpiRow>

          {/* Inline drill panels for Velocity / Win Rate comparison */}
          {!overview.influence_cutover && expandedCard === 'velocity' && drillRows && (
            <ComparisonDrillPanel
              title="Velocity Multiplier — Attendees vs Non-Attendees"
              rows={drillRows}
              onClose={() => setExpandedCard(null)}
            />
          )}
          {!overview.influence_cutover && <WinRateDrillPanel
            bands={overview?.win_rate_bands}
            regression={overview?.win_rate_regression}
            open={expandedCard === 'winrate'}
            onClose={() => setExpandedCard(null)}
            attendeeWinRate={overview?.win_rate_attendees}
            baselineWinRate={overview?.win_rate_baseline}
            attendeeN={overview?.win_rate_attendee_n_participant_pool}
            baselineN={overview?.win_rate_baseline_n}
            attendeeOpenShare={overview?.win_rate_attendee_open_share}
            baselineOpenShare={overview?.win_rate_baseline_open_share}
            attendeeAvgDealSize={overview?.attendee_avg_deal_size}
            baselineAvgDealSize={overview?.baseline_avg_deal_size}
          />}

          {botPanelOpen && !overview.influence_cutover && botSummary?.domains?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>Bot &amp; Suspicious Participants by Domain</Typography>
                <ExportMenu title="Bot and suspicious participants by domain" data={botSummary.domains} columns={BOT_EXPORT_COLUMNS} />
                <Button size="small" onClick={() => setBotPanelOpen(false)} sx={{ minWidth: 0, p: 0.5 }}>✕</Button>
              </Box>
              <Table size="small" aria-label="Bot participants by domain">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    {['Domain', 'Status', 'Participants', 'Bot Score', 'Reason'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {botSummary.domains.map((row, i) => (
                    <TableRow key={row.domain || i}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.domain}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: row.status === 'bot' ? '#d32f2f' : '#ed6c02', fontWeight: 600 }}>{row.status}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.participant_count}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{row.bot_score != null ? row.bot_score.toFixed(2) : '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Drill panels for pipeline segments and won */}
          {pipelineDrillConfig && startDate && endDate && (
            <DrillPanel
              key={`${expandedCard}|${attachmentThreshold}`}
              title={pipelineDrillTitle[expandedCard] || 'Opportunities'}
              startDate={startDate}
              endDate={endDate}
              windowMonths={18}
              panelConfig={pipelineDrillConfig}
              onClose={() => setExpandedCard(null)}
            />
          )}

          {/* Top 10 workshops table: with the influence cutover ON the Workshop Index on Deep Dive carries the workshop view (plans 2194/2200) */}
          {!overview.influence_cutover && (<>
          <Paper elevation={1} sx={{ p: 2, mb: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Top Workshops by Program Influence
                </Typography>
                {overview?.total_workshops > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Top 10 of {overview.total_workshops} total workshops in program
                  </Typography>
                )}
              </Box>
              {workshops != null && workshops.length > 0 && (
                <ExportMenu
                  title="Top workshops by direct opportunity value"
                  data={workshops.map((w, i) => ({ ...w, rank: i + 1, win_rate: fmtPct(w.win_rate), open_share_text: w.open_share != null ? `${Math.round(w.open_share * 100)}%` : '' }))}
                  columns={WORKSHOP_EXPORT_COLUMNS}
                />
              )}
              <PageGuideTooltip title="Workshop ranking" description="Total opportunity value and Won = full Salesforce deal value (all stages) of every opportunity linked to an attendee of the workshop (all products), direct only: SE + external-customer attendees, excluding partner-account opportunities. Tagged opp. value and Tagged Won count only line items covered by the workshop's own product tags (the Deep Dive view). See Channel Partner Sourced above for the program-level partner total opportunity value figure. Click any row for linked opportunities." metricKey="workshop_ranking" />
            </Box>
            {workshops == null && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
            )}
            {workshops != null && workshops.length === 0 && (
              <Typography variant="body2" color="text.secondary">No workshop data for this period.</Typography>
            )}
            {workshops != null && workshops.length > 0 && (
              <Table size="small" aria-label="Top Workshops table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: 36 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Workshop</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Attendees</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Total opp. value</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Won</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Tagged opp. value</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Tagged Won</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>Win Rate</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textAlign: 'right' }}>$/Attendee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workshops.map((row, idx) => (
                    <TableRow
                      key={row.name}
                      hover
                      onClick={() => toggleWorkshop(row.name)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: expandedWorkshop === row.name ? '#e3f2fd' : undefined,
                        '&:nth-of-type(odd)': { backgroundColor: expandedWorkshop === row.name ? '#e3f2fd' : '#fafafa' },
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <WorkshopLink id={row.workshop_id} name={row.name} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right' }}>{row.attendees}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right', color: '#1976d2', fontWeight: 600 }}>
                        {fmtCurrency(row.pipeline)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right', color: '#2e7d32' }}>
                        {fmtCurrency(row.won)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right' }}>
                        {fmtCurrency(row.attributed_pipeline)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right' }}>
                        {fmtCurrency(row.attributed_won)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right' }}>
                        <WinRateWithOpen rate={row.win_rate} n={row.win_rate_n} openShare={row.open_share} variant="caption" align="right" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', textAlign: 'right' }}>
                        {fmtCurrency(row.pipeline_per_attendee)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {workshops != null && workshops.length > 0 && (
              <Typography variant="caption" color="text.secondary" data-testid="top-workshops-overlap-note" sx={{ display: 'block', mt: 0.5 }}>
                Workshops overlap — an opportunity shared by several workshops is credited in full to each, so rows do not sum to the program total.
              </Typography>
            )}
            {workshopDrillConfig && startDate && endDate && (
              <DrillPanel
                key={expandedWorkshop}
                title={`Opportunities — ${expandedWorkshop}`}
                startDate={startDate}
                endDate={endDate}
                windowMonths={18}
                panelConfig={workshopDrillConfig}
                onClose={() => setExpandedWorkshop(null)}
              />
            )}
          </Paper>
          </>)}

          {kpiCardsOn ? (
          <Alert severity="info" sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }} aria-label="How we measure the lift">
            <strong>How we measure the lift:</strong> the win-rate difference between deals where an SE or technical team member attended TEC before the deal closed and comparable deals with no such attendee, at the same age and deal size, closed deals only, with the share still open and a 95% interval shown beside it. It is an association, not proof that TEC changed the outcome, and the influenced dollar figures are reach, not revenue TEC created.{' '}
            <Button size="small" onClick={() => navigate('/methodology?metric=pooled_lift')} sx={{ p: 0, fontSize: '0.75rem', textTransform: 'none', verticalAlign: 'baseline' }}>
              Why trust this number? →
            </Button>
          </Alert>
          ) : (
          <Alert severity="info" sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }}>
            <strong>How we measure the multiplier:</strong> (win rate × average won deal) for attendee-linked deals divided by the same for baseline deals, which are Salesforce opportunities with no linked TEC check-in (an audit found some had an attending SE on the team). It is mostly a deal-size ratio and contains no time, so it is not a speed measure and not proof that TEC made deals bigger. Attendee win rate is often lower than baseline; that can reflect which deals SEs join, not TEC's effect.{' '}
            <Button size="small" onClick={() => navigate('/methodology?metric=velocity_multiplier')} sx={{ p: 0, fontSize: '0.75rem', textTransform: 'none', verticalAlign: 'baseline' }}>
              Why trust this number? →
            </Button>
          </Alert>
          )}
        </>
      )}

      {/* Attachment Rate when overview not loaded yet but dates are available */}
      {!overview && startDate && endDate && !overviewLoading && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ flex: '1 1 180px', maxWidth: 280 }}>
            <AttachmentRateCard
              startDate={startDate}
              endDate={endDate}
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Quarterly trend chart */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {trendOn ? 'TEC-Influenced Deal Value & Win Rate — Quarterly Trend' : 'Total Opportunity Value & Win Rate — Quarterly Trend'}
      </Typography>
      {trendOn ? (
        <Typography variant="caption" color="text.secondary" display="block" data-testid="trend-basis" sx={{ mb: 1 }}>
          Basis: TEC-influenced deals by created quarter, from 2023-Q3 (when attendee data begins). Win rate is the raw closed-only rate with n and open share, not the equal-age lift; recent quarters are mostly open. Reach, not revenue.
        </Typography>
      ) : (overview?.influence_cutover && (
        <Typography variant="caption" color="warning.main" display="block" data-testid="trend-fallback" sx={{ mb: 1 }}>
          {trendReloading ? reloadingText(trendBlock.data) : <>Basis: {PREVIOUS_BASIS}.{blockFallbackReason(trendBlock) ? ` Influenced view unavailable: ${blockFallbackReason(trendBlock)}.` : ''}</>}
        </Typography>
      ))}
      {(trendLoading || (overview?.influence_cutover && trendBlock.loading)) && <CircularProgress size={24} />}
      {trendOption && (
        <Paper elevation={1} sx={{ p: 2 }}>
          <ReactECharts
            option={trendOption}
            style={{ height: 300 }}
            notMerge
            aria-label="Quarterly trend chart"
            onEvents={trendOn && summaryDrillOn ? { click: onTrendBarClick } : undefined}
          />
          {trendOn && summaryDrillOn && (
            <Typography variant="caption" color="text.secondary" display="block" data-testid="trend-drill-hint">
              Click a bar for the influenced deals created in that quarter; the list adds up to the bar.
            </Typography>
          )}
          {trendOn && summaryDrillOn && trendDrill && (
            <DrillPanel
              key={`${trendDrill.of}|${trendDrill.state}`}
              title={trendDrill.title}
              filters={{}}
              windowMonths={0}
              postWorkshopOnly={false}
              panelConfig={makeInfluenceDrillConfig(trendDrill.of, { tecProgramTagId: tecProgramFilter, surface: 'summary' }, { state: trendDrill.state })}
              onClose={() => setTrendDrill(null)}
            />
          )}
          {trendMature?.anyYoung && (
            <Typography variant="caption" color="text.secondary" display="block" data-testid="trend-young-note">
              Win rate line: {YOUNG_QUARTERS_NOTE}. Bars show every quarter.
            </Typography>
          )}
        </Paper>
      )}
      {!trendLoading && !trendBlock.loading && !trendOption && (
        <Typography variant="body2" color="text.secondary">No trend data available.</Typography>
      )}
    </Box>
  );
}
